import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PaginatedData, Pagination } from 'app/shared/shared.types';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApprovalStatusDTO, RejectionFormData, Release, ReleaseApproval, ReleaseHistory, ReleaseHistoryEntry, ReleaseStatus, Store, TakeDownDTO } from 'app/modules/user/releases/releases.types';
import moment from 'moment/moment';

@Injectable({
    providedIn: 'root'
})
export class ReleaseApprovalService {

    private _release: BehaviorSubject<Release | null> = new BehaviorSubject(null);
    private _pendingCount: BehaviorSubject<{express: number, normal: number}> = new BehaviorSubject({express: 0, normal: 0});
    private _paginatedReleases: BehaviorSubject<PaginatedData<Release>> = new BehaviorSubject(null);
    private _paginatedReleaseHistory: BehaviorSubject<PaginatedData<ReleaseHistoryEntry>> = new BehaviorSubject(null);

    lastPendingCountUpdate: number = 0;

    constructor(private _httpClient: HttpClient) {
    }

    get release$(): Observable<Release> {
        return this._release.asObservable();
    }

    get paginatedReleases$(): Observable<PaginatedData<Release>> {
        return this._paginatedReleases.asObservable();
    }

    get paginatedReleaseHistory$(): Observable<PaginatedData<ReleaseHistoryEntry>> {
        return this._paginatedReleaseHistory.asObservable();
    }

    get pendingCount$(): Observable<{express: number, normal: number}> {
        return this._pendingCount.asObservable();
    }

    getPendingReleasesCount(): Observable<{express: number, normal: number}> {
        return this._httpClient.get<{express: number, normal: number}>('api/release/pending-count').pipe(
            tap((count) => {
                this._pendingCount.next(count);
            })
        );
    }


    getPaginatedReleases(page: number = 0, size: number = 10, sort: string = 'title', order: 'asc' | 'desc' | '' = 'desc', search: string = '', status: string = 'pending', partnerId?: string):
        Observable<PaginatedData<Release>> {
        return this._httpClient.get<PaginatedData<Release>>('api/release/paginated', {
            params: {
                page: '' + page,
                size: '' + size,
                sort,
                order,
                search,
                status,
                partnerId,
            }
        }).pipe(
            tap((response: any) => {
                this._paginatedReleases.next(response);
            })
        );
    }

    getPaginatedReleaseHistory(page: number = 0, size: number = 10, id: string):
        Observable<PaginatedData<ReleaseHistoryEntry>> {
        return this._httpClient.get<PaginatedData<ReleaseHistoryEntry>>('api/release/by-uuid/'+id+'/paginated-history', {
            params: {
                page: '' + page,
                size: '' + size
            }
        }).pipe(
            tap((response: any) => {
                this._paginatedReleaseHistory.next(response);
            })
        );
    }

    getReleaseById(id: string): Observable<Release> {
        return this._httpClient.get<Release>('api/release/by-uuid/'+id).pipe(
            tap((release) => {
                this._release.next(release);
            })
        );
    }

    updateCurrentReleaseSubject(release: Release) {
        this._release.next(release);
    }

    getNextPendingApproval(approvalId: string) {
        return this._httpClient.get<Release>('api/release/next-approval', {params: {current: approvalId}}).pipe(
            tap((response) => {
                if (response?.uuid) {
                    this._release.next(response);
                }
            })
        );
    }

    approveRelease(approvalId: string) {
        return this.setApprovalAction([approvalId], 'approve').pipe(
            tap((response) => {
                this.getPendingReleasesCount().subscribe();
                if (response?.uuid) {
                    this._release.next(response);
                }
            })
        );
    }

    rejectReleases(approvalIds: string[], formData?: RejectionFormData) {
        return this.setApprovalAction(approvalIds, 'reject', formData).pipe(
            tap((response) => {
                this.getPendingReleasesCount().subscribe();
                if (approvalIds.length === 1 && response?.uuid) {
                    this._release.next(response);
                }
            })
        );
    }

    takeDownReleases(releaseIds: string[], formData?: RejectionFormData) {
        let body: TakeDownDTO = {releases: releaseIds};
        if (formData) {
            body.reason = formData.rejectionReason;
            body.permanent = formData.permanentRejection;
        }

        return this._httpClient.post<Release>('api/release/take-down', body).pipe(
            tap(response => {
                if (releaseIds.length === 1 && response) {
                    this._release.next(response);
                }
            })
        );

    }

    rescanRelease(releaseId: string) {
        return this._httpClient.post<Release>('api/release/by-uuid/'+releaseId+'/rescan', {}).pipe
        (tap(_ => {
            let release = this._release.getValue();
            release.tracks.forEach(track => {
                track.acrCloudResults = [];
            });
            release.waitingForFingerprinting = true;
            this._release.next(release);
        }));
    }

    setReleaseStores(releaseId: string, stores: Store[]) {
        return this._httpClient.patch<Release>('api/release/by-uuid/'+releaseId+'/stores', stores).pipe(
            tap((response) => {
                this._release.next(response);
            })
        );
    }

    private setApprovalAction(approvalIds: string[], action: 'approve' | 'reject', rejectionFormData?: RejectionFormData) {
        let body: ApprovalStatusDTO = {approvals: approvalIds};
        if (action === 'reject' && rejectionFormData) {
            body.rejectionReason = rejectionFormData.rejectionReason;
            body.permanentRejection = rejectionFormData.permanentRejection;
        }

        return this._httpClient.post<Release>('api/release/approval/' + action, body).pipe(
            tap(response => {
                if (approvalIds.length === 1 && response) {
                    this._release.next(response);
                }

            })
        );
    }
}
